import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProviders, getEntries, createEntry, deleteEntry, calculateDays } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PenTool, AlertCircle, Trash2, ClipboardList, XCircle } from 'lucide-react';

const EntriesPage = () => {
  const { isAdmin, user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualForm, setShowManualForm] = useState(false);
  
  // Manual form state
  const [manualProvider, setManualProvider] = useState('');
  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [manualCalc, setManualCalc] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (manualStart && manualEnd && manualStart <= manualEnd) {
      updateManualCalc();
    } else {
      setManualCalc(null);
    }
  }, [manualStart, manualEnd]);

  const loadData = async () => {
    try {
      const [providersData, entriesData] = await Promise.all([
        getProviders(),
        getEntries()
      ]);
      setProviders(providersData);
      setEntries(entriesData.sort((a, b) => new Date(b.start_date) - new Date(a.start_date)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateManualCalc = async () => {
    try {
      const result = await calculateDays(manualStart, manualEnd);
      setManualCalc(result);
    } catch (error) {
      console.error('Error calculating days:', error);
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    
    if (!manualProvider || !manualStart || !manualEnd) {
      alert('Preencha todos os campos');
      return;
    }
    
    if (manualStart > manualEnd) {
      alert('Data final anterior à inicial');
      return;
    }
    
    try {
      await createEntry({
        provider_id: manualProvider,
        start_date: manualStart,
        end_date: manualEnd
      }, true);
      
      // Reset form
      setManualProvider('');
      setManualStart('');
      setManualEnd('');
      setManualCalc(null);
      setShowManualForm(false);
      
      loadData();
      alert('Ajuste manual lançado com sucesso.');
    } catch (error) {
      alert('Erro ao lançar ajuste');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apagar este registro?')) {
      try {
        await deleteEntry(id);
        loadData();
      } catch (error) {
        alert('Erro ao excluir registro');
      }
    }
  };

  const getProviderName = (id) => {
    const provider = providers.find(p => p.id === id);
    return provider?.name || 'Desconhecido';
  };

  const formatDate = (dateStr) => {
    // Parse date string directly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in">
      <header className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Histórico & Ajustes</h2>
          <p className="text-slate-500 text-sm">Registro de indisponibilidades confirmadas e lançamentos manuais.</p>
        </div>
        
        {isAdmin() && (
          <Button 
            onClick={() => setShowManualForm(!showManualForm)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="toggle-manual-form"
          >
            <PenTool className="h-4 w-4 mr-2" /> Lançamento Manual / Ajuste
          </Button>
        )}
      </header>

      {/* Manual Entry Form */}
      {showManualForm && isAdmin() && (
        <Card className="border-orange-200 bg-orange-50" data-testid="manual-entry-form">
          <CardHeader>
            <CardTitle className="font-heading text-sm text-orange-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Lançamento de Indisponibilidade Não Comunicada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-orange-700 mb-4">
              Use para registrar dias em que o parceiro não prestou serviços sem comunicação prévia.
            </p>
            <form onSubmit={handleManualEntry} className="flex gap-4 items-end flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs font-semibold text-orange-800">Parceiro</Label>
                <Select value={manualProvider} onValueChange={setManualProvider}>
                  <SelectTrigger className="border-orange-300 bg-white" data-testid="manual-provider">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-orange-800">Início</Label>
                <Input 
                  type="date" 
                  value={manualStart}
                  onChange={(e) => setManualStart(e.target.value)}
                  className="border-orange-300 bg-white"
                  data-testid="manual-start"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-orange-800">Fim</Label>
                <Input 
                  type="date" 
                  value={manualEnd}
                  onChange={(e) => setManualEnd(e.target.value)}
                  className="border-orange-300 bg-white"
                  data-testid="manual-end"
                />
              </div>
              {manualCalc && (
                <div className="text-xs text-orange-800 font-bold self-center px-2">
                  {manualCalc.days} dias ({manualCalc.type})
                </div>
              )}
              <Button type="submit" className="bg-orange-700 hover:bg-orange-800" data-testid="manual-submit">
                Confirmar Lançamento
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* History Log */}
      <Card className="border-slate-200" data-testid="entries-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 font-medium uppercase text-xs tracking-wide">
                <tr>
                  <th className="px-6 py-3">Parceiro</th>
                  <th className="px-6 py-3">Período</th>
                  <th className="px-6 py-3 text-center">Dias</th>
                  <th className="px-6 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`entry-row-${e.id}`}>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {getProviderName(e.provider_id)}
                      {e.is_manual && (
                        <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1 py-0.5 rounded">Manual</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs">
                      {formatDate(e.start_date)} até {formatDate(e.end_date)}
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-indigo-600 bg-indigo-50/50 rounded">
                      {e.days}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {isAdmin() ? (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                          onClick={() => handleDelete(e.id)}
                          data-testid={`delete-entry-${e.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        // Provider can cancel their own approved entries
                        e.provider_id === user?.provider_id && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDelete(e.id)}
                            data-testid={`cancel-entry-${e.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Cancelar
                          </Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && (
              <div className="p-12 text-center text-slate-400">
                <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nenhum histórico encontrado.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EntriesPage;
